import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Landing + auth flow ship eager — they're the first thing every visitor sees.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LegalFooter from './components/LegalFooter';
import FullScreenLoader from './components/FullScreenLoader';
import ErrorBoundary from './components/ErrorBoundary';

// Heavy authenticated pages + legal pages are code-split.
// Each becomes its own chunk that only downloads when the route is visited.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RIASECAssessment = lazy(() => import('./pages/RIASECAssessment'));
const SkillsAssessment = lazy(() => import('./pages/SkillsAssessment'));
const CareerReport = lazy(() => import('./pages/CareerReport'));
const ResumeDownload = lazy(() => import('./pages/ResumeDownload'));
const ResumeInformation = lazy(() => import('./pages/ResumeInformation'));
const ComprehensiveReport = lazy(() => import('./pages/ComprehensiveReport'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const ShippingPolicy = lazy(() => import('./pages/legal/ShippingPolicy'));
const ContactUs = lazy(() => import('./pages/legal/ContactUs'));

const RouteSuspense = () => (
  <FullScreenLoader
    title="Loading page…"
    message="Fetching just the code you need."
    testId="route-suspense-loader"
  />
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<RouteSuspense />}>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/assessment/riasec"
            element={
              <PrivateRoute>
                <RIASECAssessment />
              </PrivateRoute>
            }
          />
          <Route
            path="/assessment/skills"
            element={
              <PrivateRoute>
                <SkillsAssessment />
              </PrivateRoute>
            }
          />
          <Route
            path="/career-report"
            element={
              <PrivateRoute>
                <CareerReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/resume"
            element={
              <PrivateRoute>
                <ResumeInformation />
              </PrivateRoute>
            }
          />
          <Route
            path="/resume-information"
            element={
              <PrivateRoute>
                <ResumeInformation />
              </PrivateRoute>
            }
          />
          <Route
            path="/resume-download"
            element={
              <PrivateRoute>
                <ResumeDownload />
              </PrivateRoute>
            }
          />
          <Route
            path="/comprehensive-report"
            element={
              <PrivateRoute>
                <ComprehensiveReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <PrivateRoute>
                <JobsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <PrivateRoute>
                <BlogAdmin />
              </PrivateRoute>
            }
          />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<TermsAndConditions />} />
          <Route path="/legal/refund" element={<RefundPolicy />} />
          <Route path="/legal/shipping" element={<ShippingPolicy />} />
          <Route path="/legal/contact" element={<ContactUs />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
        <LegalFooter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
